import React, { useEffect, useState } from "react";
import { Loader2, PackageCheck, Truck, XCircle } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { dateLocale } from "@/lib/dateTime";

interface Progress {
  complete: boolean;
  items: { order_line_id: string; product_name: string; variant_label?: string | null; unit: string; ordered: number; dispatched: number; delivered: number; remaining: number }[];
  deliveries: { id: string; seq: number; delivery_status: "DISPATCHED" | "DELIVERED" | "CANCELLED"; dispatched_on: string; courier?: string | null; tracking_number?: string | null; expected_on?: string | null; delivered_at?: string | null; received_by?: string | null; cancel_reason?: string | null; items: { order_line_id: string; quantity: number }[] }[];
}

const day = (d?: string | null) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" }) : "");

/** What was ordered against what has gone out and arrived, the consignments, and the form to send the next one */
export function OrderDeliveries({ orderId, status, canEdit, onChanged, onLoaded }: { orderId: string; status: string; canEdit: boolean; onChanged: () => void; onLoaded: (hasDeliveries: boolean) => void }) {
  const { success, error } = useToast();
  const [p, setP] = useState<Progress | null>(null);
  const [busy, setBusy] = useState(false);
  const [send, setSend] = useState<null | { qty: Record<string, string>; courier: string; tracking: string; expected: string; note: string }>(null);
  const [arrive, setArrive] = useState<null | { id: string; seq: number; by: string }>(null);
  const [cancel, setCancel] = useState<null | { id: string; seq: number; reason: string }>(null);

  const load = async () => {
    try {
      const d = unwrapObject<Progress>(await api.get(`/api/SalesOrder/Deliveries/${orderId}`));
      setP(d);
      onLoaded(d.deliveries.some((x) => x.delivery_status !== "CANCELLED"));
    } catch {
      setP(null);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, status]);

  if (!p) return null;
  const sendable = canEdit && (status === "CONFIRMED" || status === "IN_PROGRESS") && p.items.some((i) => i.remaining > 0);
  if (status === "DRAFT" || (status === "CANCELLED" && p.deliveries.length === 0)) return null;

  const run = async (fn: () => Promise<any>, done: string, close: () => void) => {
    setBusy(true);
    try {
      await fn();
      success(done, "");
      close();
      await load();
      onChanged();
    } catch (e: any) {
      error("Could not do that", e?.message || "Try again");
    } finally {
      setBusy(false);
    }
  };

  const openSend = () => setSend({ qty: Object.fromEntries(p.items.map((i) => [i.order_line_id, i.remaining > 0 ? String(i.remaining) : ""])), courier: "", tracking: "", expected: "", note: "" });
  const chosen = send ? p.items.filter((i) => Number(send.qty[i.order_line_id]) > 0) : [];
  const nameOf = (lineId: string) => p.items.find((i) => i.order_line_id === lineId)?.product_name || "";

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Truck className="size-4 text-primary" />
          <h3 className="text-sm font-semibold">Deliveries</h3>
          {p.complete && <Badge variant="success">Everything delivered</Badge>}
        </div>
        {sendable && <Button size="sm" variant="outline" onClick={openSend} disabled={busy}><PackageCheck className="size-4 mr-1.5" /> Send items</Button>}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] border-collapse text-[12.5px]">
          <thead><tr className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground"><th className="px-3 py-2 text-left">Item</th><th className="px-3 py-2 text-right">Ordered</th><th className="px-3 py-2 text-right">On the road</th><th className="px-3 py-2 text-right">Arrived</th><th className="px-3 py-2 text-right">To send</th></tr></thead>
          <tbody>
            {p.items.map((i) => (
              <tr key={i.order_line_id} className="border-t border-border/60">
                <td className="px-3 py-2 font-medium">{i.product_name}{i.variant_label ? ` · ${i.variant_label}` : ""}</td>
                <td className="px-3 py-2 text-right">{i.ordered} {i.unit}</td>
                <td className="px-3 py-2 text-right">{i.dispatched || "-"}</td>
                <td className="px-3 py-2 text-right">{i.delivered || "-"}</td>
                <td className={`px-3 py-2 text-right ${i.remaining > 0 ? "font-semibold" : "text-muted-foreground"}`}>{i.remaining || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {p.deliveries.length > 0 && (
        <ul className="divide-y border-t">
          {p.deliveries.map((d) => (
            <li key={d.id} className={`flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-[13px] ${d.delivery_status === "CANCELLED" ? "opacity-60" : ""}`}>
              <div className="min-w-0">
                <div className="font-medium">
                  Delivery {d.seq} <span className="text-xs font-normal text-muted-foreground">· sent {day(d.dispatched_on)}{d.expected_on ? ` · expected ${day(d.expected_on)}` : ""}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {d.items.map((i) => `${i.quantity} × ${nameOf(i.order_line_id)}`).join(", ")}
                  {(d.courier || d.tracking_number) && ` · ${[d.courier, d.tracking_number].filter(Boolean).join(" ")}`}
                  {d.delivery_status === "DELIVERED" && ` · arrived ${day(d.delivered_at)}${d.received_by ? `, ${d.received_by}` : ""}`}
                  {d.delivery_status === "CANCELLED" && ` · cancelled: ${d.cancel_reason}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {d.delivery_status === "DELIVERED" && <Badge variant="success">Arrived</Badge>}
                {d.delivery_status === "CANCELLED" && <Badge variant="outline">Cancelled</Badge>}
                {d.delivery_status === "DISPATCHED" && <Badge variant="blue">On the road</Badge>}
                {d.delivery_status === "DISPATCHED" && canEdit && (
                  <>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setArrive({ id: d.id, seq: d.seq, by: "" })} disabled={busy}>Mark arrived</Button>
                    <Button size="icon-sm" variant="ghost" aria-label={`Cancel delivery ${d.seq}`} onClick={() => setCancel({ id: d.id, seq: d.seq, reason: "" })} disabled={busy}><XCircle className="size-4 text-danger" /></Button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!send} onOpenChange={(o) => !o && setSend(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Send items</DialogTitle><DialogDescription>Choose how much of each item goes out in this consignment. The rest stays to be sent.</DialogDescription></DialogHeader>
          {send && (
            <div className="space-y-3">
              {p.items.filter((i) => i.remaining > 0).map((i) => (
                <div key={i.order_line_id} className="grid grid-cols-[1fr_110px] items-center gap-3 text-sm">
                  <div><div className="font-medium">{i.product_name}</div><div className="text-xs text-muted-foreground">{i.remaining} {i.unit} left to send</div></div>
                  <Input type="number" min={0} max={i.remaining} step="0.01" value={send.qty[i.order_line_id]} onChange={(e) => setSend({ ...send, qty: { ...send.qty, [i.order_line_id]: e.target.value } })} aria-label={`Quantity of ${i.product_name}`} />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Courier</Label><Input value={send.courier} onChange={(e) => setSend({ ...send, courier: e.target.value })} maxLength={100} /></div>
                <div className="space-y-1.5"><Label>Tracking number</Label><Input value={send.tracking} onChange={(e) => setSend({ ...send, tracking: e.target.value })} maxLength={100} /></div>
              </div>
              <div className="space-y-1.5"><Label>Expected on</Label><Input type="date" value={send.expected} onChange={(e) => setSend({ ...send, expected: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Note</Label><Textarea rows={2} value={send.note} onChange={(e) => setSend({ ...send, note: e.target.value })} maxLength={1000} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSend(null)}>Cancel</Button>
            <Button
              disabled={busy || chosen.length === 0 || (send ? chosen.some((i) => Number(send.qty[i.order_line_id]) > i.remaining) : true)}
              onClick={() => send && run(() => api.post(`/api/SalesOrder/Dispatch/${orderId}`, { items: chosen.map((i) => ({ order_line_id: i.order_line_id, quantity: Number(send.qty[i.order_line_id]) })), courier: send.courier.trim() || undefined, tracking_number: send.tracking.trim() || undefined, expected_on: send.expected || undefined, note: send.note.trim() || undefined }), "Consignment sent", () => setSend(null))}
            >{busy && <Loader2 className="size-4 animate-spin mr-2" />}Send {chosen.length || ""} {chosen.length === 1 ? "item" : "items"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!arrive} onOpenChange={(o) => !o && setArrive(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Delivery {arrive?.seq} arrived</DialogTitle><DialogDescription>When everything ordered has arrived, the order is marked delivered.</DialogDescription></DialogHeader>
          <div className="space-y-1.5"><Label>Who signed for it / proof of delivery</Label><Input value={arrive?.by || ""} onChange={(e) => arrive && setArrive({ ...arrive, by: e.target.value })} maxLength={300} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArrive(null)}>Back</Button>
            <Button disabled={busy} onClick={() => arrive && run(() => api.patch(`/api/SalesOrder/Delivery/${arrive.id}/Delivered`, { received_by: arrive.by.trim() || undefined }), "Marked as arrived", () => setArrive(null))}>{busy && <Loader2 className="size-4 animate-spin mr-2" />}Mark arrived</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancel} onOpenChange={(o) => !o && setCancel(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Cancel delivery {cancel?.seq}</DialogTitle><DialogDescription>Its items go back to "to send".</DialogDescription></DialogHeader>
          <div className="space-y-1.5"><Label>Reason *</Label><Textarea rows={2} value={cancel?.reason || ""} onChange={(e) => cancel && setCancel({ ...cancel, reason: e.target.value })} maxLength={500} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancel(null)}>Back</Button>
            <Button variant="destructive" disabled={busy || !cancel?.reason.trim()} onClick={() => cancel && run(() => api.patch(`/api/SalesOrder/Delivery/${cancel.id}/Cancel`, { reason: cancel.reason.trim() }), "Delivery cancelled", () => setCancel(null))}>{busy && <Loader2 className="size-4 animate-spin mr-2" />}Cancel delivery</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
